import type { EnnismoreTheme } from '@/ui/theme';

const fonts = {
  headline: 'Canela',
  intro: 'Canela',
  subheader: 'Arboria',
  body: 'Acumin',
};

export const ourHabitasTheme: EnnismoreTheme = {
  themeKey: 'ourhabitas',
  logoUrl: '/brands/ourhabitas/images/logo.svg',
  faviconUrl: '/brands/ourhabitas/images/favicon.png',
  space: ['0px', '8px', '16px', '20px', '24px', '32px', '40px', '56px'],
  fonts,
  text: {
    heading1: {
      fontFamily: fonts.headline,
      fontSize: '32px',
      lineHeight: '34px',
      fontWeight: 300,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    },
    heading2: {
      fontFamily: fonts.headline,
      fontSize: '24px',
      lineHeight: '64px',
      fontWeight: 400,
    },
    heading3: {
      fontFamily: fonts.headline,
      fontSize: '24px',
      lineHeight: '48px',
    },
    heading4: {
      fontFamily: fonts.headline,
      fontSize: '20px',
      lineHeight: '40px',
    },
    heading5: {
      fontFamily: fonts.headline,
      fontSize: '18px',
      lineHeight: '35px',
      fontWeight: 300,
    },
    heading6: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '22px',
      fontWeight: 300,
    },
    intro1: {
      fontFamily: fonts.intro,
      fontSize: '32px',
      lineHeight: '34px',
      fontWeight: 300,
      letterSpacing: '0.04em',
    },
    intro2: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '34px',
      fontWeight: 300,
      letterSpacing: '0.04em',
    },
    subheader1: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '26px',
      fontWeight: 300,
      letterSpacing: '0.04em',
    },
    subheader2: {
      fontFamily: fonts.subheader,
      fontSize: '16px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    subheader3: {
      fontFamily: fonts.subheader,
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    bodyLarge: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    bodyCopy: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    bodySmall: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    bodyExtraSmall: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    captions: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
  },
  colors: {
    white: '#FFFFFF',
    textPrimary: '#444',
    textSecondary: '#C0BAB2',
    background: '#F7F6F5',
    primary100: '#F7F6F5',
    primary200: '#E4E2DE',
    primary300: '#C0BAB2',
    primary400: '#796C5C',
    primary500: '#444444',
    secondary100: '#FFFFFF',
    secondary200: '#C0BAB2',
    secondary300: '#796C5C',
    secondary400: '#444444',
    error: '#FF0000',
    sale: '#FF0000',
  },
  buttons: {
    primary: {
      backgroundColor: '#796C5C',
      borderRadius: '0px',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      fontFamily: fonts.subheader,
      fontWeight: '400',
      fontSize: '14px',
      letterSpacing: '0.31em',
      ':hover': {
        backgroundColor: '#897C6C',
      },
      ':disabled': {
        opacity: 1,
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      borderRadius: '0px',
      border: '1px solid #796C5C',
      color: '#796C5C',
      textTransform: 'uppercase',
      fontFamily: fonts.subheader,
      fontWeight: '400',
      fontSize: '14px',
      letterSpacing: '0.31em',
      ':hover': {
        color: '#FFFFFF',
        backgroundColor: '#796C5C',
      },
    },
  },
  keylines: {
    border: '1px solid #C0BAB2',
  },
  forms: {
    label: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
      color: '#444',
    },
    heading: {
      fontFamily: fonts.headline,
      fontSize: '24px',
      lineHeight: '34px',
      letterSpacing: '0.96px',
      fontWeight: 300,
      color: '#796C5C',
    },
    input: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: 300,
      letterSpacing: '0.02em',
      borderRadius: '0px',
      border: '1px solid #796C5C',
      backgroundColor: '#FFFFFF',
      color: '#444',
    },
    checkoutForm: {
      billingInformationDescription: {
        opacity: 1,
      },
    },
  },
  fontFaceDefinitions: [
    {
      fontFamily: 'Acumin',
      fontWeight: 400,
      fontStyle: 'normal',
      sources: {
        woff2:
          'https://use.typekit.net/af/1b8691/00000000000000003b9acb3d/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n3&v=3',
      },
    },
    {
      fontFamily: 'Arboria',
      fontWeight: 400,
      fontStyle: 'normal',
      sources: {
        woff2: '/brands/ourhabitas/fonts/Arboria-Book.woff2',
      },
    },
    {
      fontFamily: 'Arboria',
      fontWeight: 500,
      fontStyle: 'normal',
      sources: {
        woff2: '/brands/ourhabitas/fonts/Arboria-Medium.woff2',
      },
    },
    {
      fontFamily: 'Canela',
      fontWeight: 300,
      fontStyle: 'normal',
      sources: {
        woff2: '/brands/ourhabitas/fonts/Canela-Light.woff2',
      },
    },
  ],
  componentProperties: {
    confirmation: {
      mobile: {
        rowGap: '20px',
      },
      title: {
        fontSize: '32px',
        lineHeight: '34px',
        letterSpacing: '1.28px',
        fontFamily: fonts.headline,
        color: 'white',
        mobile: {
          fontSize: '24px',
          padding: '0 60px',
        },
      },
      confirmationMessage: {
        color: 'white',
        fontSize: '14px',
        subtitle: {
          fontWeight: 600,
        },
        mobile: {
          fontSize: '16px',
          padding: '30px 28px 40px',
        },
      },
      header: {
        backgroundColor: '#796C5C',
        paddingBottom: '32px',
        mobile: {
          paddingBottom: '0px',
        },
      },
      bookingConfirmationDetailsCard: {
        customerNameHeading: {
          fontFamily: fonts.body,
          fontSize: '18px',
          fontWeight: 300,
          letterSpacing: '0.36px',
          color: '#2F2F2F',
        },
        bookingNumberHeading: {
          fontSize: '24px',
          fontFamily: fonts.headline,
          color: '#796C5C',
          fontWeight: 300,
          paddingBottom: '16px',
          mobile: {
            fontSize: '20px',
          },
        },
        addressHeading: {
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '0.28px',
          fontFamily: fonts.body,
          color: '#2F2F2F',
        },
        totalDataRow: {
          label: {
            fontWeight: 300,
          },
          value: {
            fontSize: '16px',
            fontWeight: 500,
          },
        },
        manageMyBooking: {
          title: {
            fontSize: '18px',
            fontFamily: fonts.body,
            paddingBottom: '12px',
            color: '#2F2F2F',
          },
          description: {
            fontSize: '14px',
            fontWeight: 300,
            lineHeight: '22px',
            letterSpacing: '0.14px',
            paddingBottom: '12px',
            color: '#2F2F2F',
          },
          link: {
            fontSize: '12px',
            textTransform: 'uppercase',
            textDecoration: 'underline',
            letterSpacing: '1.44px',
            fontFamily: fonts.subheader,
            color: '#444',
          },
        },
        paymentCancellationPolicy: {
          title: {
            fontFamily: fonts.body,
            fontSize: '18px',
            lineHeight: '30px',
            color: '#2F2F2F',
          },
          description: {
            fontSize: '14px',
            fontWeight: 300,
            lineHeight: '22px',
            letterSpacing: '0.14px',
            color: '#2F2F2F',
          },
        },
        pricePerNightTitle: {
          fontSize: '16px',
          fontWeight: 500,
        },
        termsAndConditions: {
          fontFamily: fonts.subheader,
          fontSize: '12px',
          letterSpacing: '1.44px',
          textTransform: 'uppercase',
          textDecoration: 'underline',
        },
      },
      bookingConfirmationAdditionalDetailsContainer: {
        rowGap: '35px',
        padding: '32px 32px 35px 32px',
        mobile: {
          padding: '35px 16px',
        },
      },
      bookingConfirmationContainer: {
        margin: '60px auto auto auto',
        mobile: {
          margin: '40px 16px 0',
        },
        divider: {
          borderColor: '#DCDBDC',
        },
      },
      headerImage: {
        height: '480px',
        objectFit: 'cover',
        container: {
          width: '100%',
          paddingBottom: '60px',
          margin: '0',
          mobile: {
            paddingBottom: '30px',
          },
        },
        mobile: {
          height: '220px',
        },
      },
      anyQuestionsCard: {
        mobile: {
          margin: '0 16px',
          padding: '24px 16px 35px',
        },
        title: {
          fontSize: '24px',
          lineHeight: '34px',
          letterSpacing: '0.96px',
          color: '#796C5C',
          mobile: {
            fontSize: '20px',
          },
        },
        description: {
          fontSize: '14px',
        },
      },
      thirdPartyPaymentDownloadCard: {
        description: {
          fontSize: '14px',
        },
      },
      restaurantsCard: {
        mobile: {
          margin: '0 16px',
          padding: '24px 16px 35px',
        },
        title: {
          fontSize: '24px',
          lineHeight: '34px',
          letterSpacing: '0.96px',
          color: '#796C5C',
          mobile: {
            fontSize: '20px',
          },
        },
        restaurantTitle: {
          fontSize: '18px',
        },
        restaurantDescription: {
          fontSize: '14px',
        },
      },
      whereToFindUsCard: {
        mobile: {
          margin: '0 16px',
          padding: '24px 16px 35px',
        },
        title: {
          color: '#796C5C',
          fontSize: '24px',
          lineHeight: '34px',
          letterSpacing: '0.96px',
          mobile: {
            fontSize: '20px',
          },
        },
        planYourJourneyLink: {
          color: '#2F2F2F',
        },
      },
    },
    consentInputs: {
      title: {
        display: 'none',
      },
    },
    bookingManagement: {
      pageWrapper: {
        login: { alignContent: 'center' },
        verify: { alignContent: 'center' },
      },
      title: {
        textTransform: 'uppercase',
        fontSize: '32px',
        lineHeight: '34px',
        letterSpacing: '1.28px',
        fontFamily: fonts.headline,
        fontWeight: 300,
        paddingBottom: '8px',
        color: '#796C5C',
      },
      subTitle: {
        fontSize: '18px',
        lineHeight: '22px',
        letterSpacing: '0.36px',
      },
      description: {
        fontSize: '14px',
        lineHeight: '22px',
        letterSpacing: '0.28px',
        fontWeight: 400,
      },
      button: {
        width: 'min(100%, 416px)',
      },
      input: {
        width: 'min(100%, 416px)',
      },
      bookingCard: {
        border: 'none',
        borderRadius: '0',
        body: {
          rowGap: '32px',
          padding: '32px 24px',
        },
        header: {
          borderRadius: '0',
          backgroundColor: 'white',
          borderBottom: '1px solid #C0BAB2',
          fontFamily: fonts.body,
          textTransform: 'none',
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '0.28px',
          height: '58px',
          cancelled: {
            backgroundColor: '#FFF',
            pillowBorder: 'none',
          },
        },
        footer: {
          borderTop: '1px solid #DCDBDC',
        },
        button: {
          variant: 'secondary',
        },
        cancellationModal: {
          maxWidth: '772px',
          padding: '80px 124px',
          textAlign: 'center',
          borderRadius: '0',
          title: {
            color: '#796C5C',
            textTransform: 'uppercase',
            fontSize: '32px',
            letterSpacing: '-0.96px',
            fontWeight: 300,
            paddingBottom: '40px',
          },
          body: {
            maxWidth: '50ch',
            mobile: {
              maxWidth: '100%',
            },
          },
          confirmButton: {
            width: '100%',
          },
          mobile: {
            padding: '40px 16px',
            title: {
              fontSize: '26px',
            },
          },
        },
      },
      bookingCards: {
        mobile: {
          paddingBottom: '60px',
        },
      },
    },
    header: {
      height: 80,
      borderBottom: '1px solid #C0BAB2',
      logo: {
        borderRight: 'none',
        paddingInline: '30px 0',
      },
      backgroundColor: '#F7F6F5',
      wizard: {
        step: {
          color: '#796C5C',
          textTransform: 'uppercase',
          fontSize: '14px',
          fontWeight: 400,
          opacity: 0.56,
          fontFamily: fonts.subheader,
          letterSpacing: '1.68px',
          lineHeight: '18px',
          active: {
            opacity: 1,
            fontWeight: 500,
          },
        },
      },
      languageAndCurrencySelector: {
        dropdown: {
          display: 'none',
        },
        mobile: {
          fontSize: '14px',
        },
      },
    },
    footer: {
      backgroundColor: '#FFF',
      color: '#796C5C',
      fontFamily: fonts.subheader,
      letterSpacing: '0.36px',
      fontWeight: 400,
      lineHeight: '22px',
      fontSize: '14px',
      borderTop: '1px solid #C0BAB2',
      textTransform: 'uppercase',
      copyright: {
        fontSize: '12px',
        lineHeight: '22px',
        letterSpacing: '0.24px',
      },
      mobile: {
        marginTop: '0px',
        fontSize: '14px',
      },
    },
    requestsPage: {
      paymentMethods: {
        mobile: {
          paddingBottom: '40px',
        },
      },
    },
    search: {
      title: {
        textTransform: 'uppercase',
      },
      results: {
        mobile: {
          paddingBottom: '0px',
        },
        roomImageDisclaimer: {
          mobile: {
            paddingBottom: '24px',
          },
        },
        bedroomAvailabilityList: {
          rowGap: '40px',
          mobile: {
            rowGap: '20px',
          },
        },
        roomSpecColumns: 2,
        sidebarContainer: {
          cancellationPolicy: {
            backgroundColor: 'transparent',
            border: '1px solid #D4B6C7',
            borderRadius: '6px',
            color: '#17234E',
            fontFamily: fonts.body,
            lineHeight: '22px',
            fontWeight: 300,
            title: {
              lineHeight: '30px',
              fontWeight: 400,
            },
            mobile: {
              textTransform: 'uppercase',
              color: '#444',
              fontFamily: fonts.subheader,
              letterSpacing: '1.44px',
              fontSize: '12px',
            },
            readMoreButton: {
              fontWeight: 300,
            },
          },
          hotelPerks: {
            border: 'none',
            rowGap: '24px',
            mobile: {
              backgroundColor: '#D4B6C7',
              title: {
                fontSize: '16px',
                textTransform: 'uppercase',
              },
              button: {
                textTransform: 'uppercase',
                fontSize: '14px',
                letterSpacing: '0.7px',
                lineHeight: '26px',
                opacity: 0.6,
              },
            },
          },
        },
      },
      collapsibleForm: {
        searchSummary: {
          border: 'none',
          lineHeight: '35px',
          borderRadius: '6px',
          mobile: {
            padding: '16px',
            title: {
              fontSize: '16px',
              textTransform: 'uppercase',
            },
          },
          modifySearchButton: {
            textTransform: 'uppercase',
            fontSize: '14px',
            letterSpacing: '0.7px',
            lineHeight: '26px',
            fontFamily: fonts.body,
            opacity: 0.6,
            mobile: {
              fontSize: '12px',
              lineHeight: '12px',
              letterSpacing: '1.44px',
            },
          },
          expanded: { borderRadius: '6px 6px 0 0' },
        },
        searchForm: {
          borderRadius: '0 0 6px 6px',
        },
      },
    },
    modals: {
      fontSize: '14px',
      title: {
        textTransform: 'uppercase',
        fontFamily: fonts.headline,
        fontSize: '32px',
        fontWeight: 300,
        lineHeight: '44px',
        mobile: {
          fontSize: '28px',
          lineHeight: '34px',
          letterSpacing: '0.56px',
        },
      },
    },
    calendar: {
      border: '1px solid #444444',
      borderRadius: '4px',
      boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
      color: '#444444',
      header: {
        color: '#444444',
        backgroundColor: '#FFF',
        borderBottom: '1px solid #444444',
        borderRadius: '4px 4px 0 0',
        arrow: {
          stroke: '#444444',
          top: '-1px',
        },
        caption: {
          fontWeight: 400,
          fontFamily: fonts.body,
          color: '#444444',
          letterSpacing: '0.9px',
          fontSize: '16px',
        },
        nav: {
          hover: {
            backgroundColor: 'hsl(204deg 83% 9% / 10%)',
          },
        },
      },
      body: {
        date: {
          hover: {
            backgroundColor: 'hsl(227deg 54% 20% / 30%)',
          },
          selected: { backgroundColor: '#444444' },
          selectedMiddle: { backgroundColor: '#C0BAB2' },
        },
      },
    },
    bedroomCard: {
      roomTitle: {
        fontSize: '24px',
        fontWeight: 300,
        letterSpacing: '0.72px',
        textTransform: 'uppercase',
        lineHeight: '32px',
        mobile: {
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '-0.64px',
          fontWeight: 300,
        },
      },
      roomRateName: {
        fontSize: '18px',
        letterSpacing: '0.9px',
        fontFamily: fonts.body,
        textTransform: 'uppercase',
        marginTop: '-2px',
        paddingBottom: '10px',
      },
      description: {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 400,
        letterSpacing: '0.32px',
      },
      totalPrice: {
        fontFamily: fonts.body,
        lineHeight: '24px',
      },
      totalLabel: {
        fontSize: '14px',
        letterSpacing: '0.28px',
        fontWeight: 400,
      },
      perNightPriceLabel: {
        fontSize: '14px',
        letterSpacing: '0.28px',
      },
      roomSpecItem: {
        letterSpacing: '0.28px',
        color: '#9B9B9B',
        value: {
          color: '#17234E',
          fontWeight: 400,
        },
      },
      ctaButton: {
        width: '100%',
      },
    },
    sidebarCards: {
      secondary: {
        borderRadius: '0px',
        padding: 'm',
        backgroundColor: 'background',
        borderColor: 'primary300',
        color: 'textPrimary',
      },
      primary: {
        borderRadius: '0px',
        padding: 'l',
        backgroundColor: 'primary200',
        borderColor: 'white',
        color: 'textPrimary',
      },
    },
    checkoutSidebar: {
      cancellationPolicy: {
        backgroundColor: 'transparent',
        border: '1px solid #796C5C',
        title: {
          lineHeight: '22px',
          fontSize: '18px',
          fontFamily: fonts.body,
        },
        description: {
          color: '#444',
        },
      },
    },
    halfScreenLayout: {
      borderRight: 'none',
    },
    addOnCard: {
      disclaimer: {
        borderRadius: '0px',
        warning: {
          fontSize: '16px',
          lineHeight: '22px',
          letterSpacing: '0.32px',
          fontFamily: fonts.body,
          fontWeight: 600,
        },
        rateName: {
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '0.28px',
          fontFamily: fonts.body,
          fontWeight: 400,
        },
        rateDescription: {
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '0.28px',
          fontFamily: fonts.body,
          fontWeight: 400,
        },
      },
      border: '1px solid #796C5C',
      title: {
        fontFamily: fonts.headline,
        fontSize: '18px',
        lineHeight: '26px',
        letterSpacing: '0.72px',
        textTransform: 'capitalize',
        fontWeight: 300,
      },
      subTitle: {
        fontSize: '18px',
        lineHeight: '22px',
        fontWeight: 400,
        letterSpacing: '0.36px',
      },
      description: {
        fontSize: '14px',
        lineHeight: '22px',
        fontWeight: 400,
        color: '#444',
      },
      ctaLabel: {
        fontSize: '16px',
        fontWeight: 400,
        color: '#2F2F2F',
      },
    },
    staySummary: {
      border: '1px solid #796C5C',
      title: {
        color: '#796C5C',
        fontFamily: fonts.headline,
        fontSize: '24px',
        lineHeight: '34px',
        letterSpacing: '0.96px',
        fontWeight: 300,
        mobile: {
          fontSize: '16px',
          lineHeight: '0px',
        },
      },
      summarySection: {
        padding: '24px',
        mobile: {
          padding: '20px',
        },
      },
      roomNumberHeading: {
        fontSize: '14px',
        fontFamily: fonts.body,
        lineHeight: '22px',
        letterSpacing: '0.28px',
        padding: '0 0 16px 0',
      },
      dataRow: {
        label: {
          opacity: 1,
          fontWeight: 400,
          fontSize: '14px',
        },
        value: { fontWeight: 400, fontSize: '14px' },
      },
      total: {
        value: {
          fontWeight: 500,
        },
      },
      dropdown: {
        textTransform: 'uppercase',
        fontSize: '12px',
        letterSpacing: '1.44px',
        opacity: 1,
      },
      divider: {
        borderColor: '#C0BAB2',
      },
    },
    imageSlider: {
      paginator: {
        color: '#17234E',
        line: {
          margin: '10px 10px 0 10px',
        },
      },
    },
    cards: {
      primary: {
        border: 'none',
        divider: {
          borderTop: '1px solid #DCDBDC',
        },
      },
    },
    banners: {
      backgroundColor: 'primary200',
      color: 'textPrimary',
    },
    page: {
      stepLabel: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '24px',
        letterSpacing: '0.28px',
      },
      background: '#F7F6F5',
      title: {
        color: '#796C5C',
        mobile: {
          color: '#796C5C',
          fontSize: '26px',
          lineHeight: '40px',
          letterSpacing: '-0.78px',
          fontWeight: 300,
          textTransform: 'uppercase',
          fontFamily: fonts.headline,
        },
      },
      screenLayout: {
        mobile: {
          paddingBottom: '0px',
        },
      },
    },
    hotelPerk: {
      gap: '8px',
      title: {
        fontFamily: fonts.body,
        fontWeight: 400,
        lineHeight: '22px',
      },
      description: {
        opacity: 1,
        lineHeight: '16px',
      },
    },
  },
  icons: {},
  __temporary: {
    displayCarTransportOnNewLine: true,
  },
};
